<template>
  <div class="h-[calc(100vh-178px)] flex gap-x-4" v-if="loaded">
    <ContentBlock class="basis-60" title="Filters">
      <div class="p-4 flex flex-col gap-y-4">
        <MultipleObjectSelect label="Event Type" v-model="proxyFilter.eventTypes" :options="eventTypesOptions"
          placeholder="Select Event Type" inline />
        <template v-if="arrowsAvailable">
          <RangeInput label="Max G" unit=" g" :min="maxGBounds.min" :max="maxGBounds.max" :step="MAX_G_STEP"
            v-model:minValue="proxyFilter.maxG.min" v-model:maxValue="proxyFilter.maxG.max" />
          <HourRangeInput label="Time of Day" v-model:minValue="proxyFilter.hours.min"
            v-model:maxValue="proxyFilter.hours.max" />
        </template>
        <div
          class="mt-4 flex text-zinc-50 space-x-2 items-center justify-center px-3 py-2 rounded-lg border border-accent bg-accent/75 hover:bg-accent text-sm cursor-pointer"
          @click="applyFilter">
          <span>Apply</span>
        </div>
        <!-- <div v-if="stats" class="text-xs text-zinc-500 dark:text-zinc-400">
          {{ stats.matched.toLocaleString() }} of {{ stats.total.toLocaleString() }} events matched to a road
        </div> -->
      </div>
    </ContentBlock>
    <div class="grow rounded-lg overflow-hidden h-full relative" ref="mapElement">
      <div v-if="processing"
        class="absolute inset-0 z-10 flex flex-col items-center justify-center gap-y-3 bg-zinc-900/60 text-zinc-50">
        <IconSpinner class="animate-spin w-10 h-10" />
        <span class="text-sm">{{ processingMessage }}</span>
      </div>
      <div v-if="focusedEvent"
        class="bg-zinc-100 dark:bg-zinc-800 px-4 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg shadow-md absolute right-4 top-4 w-64 text-sm">
        <div class="flex items-center gap-x-2 mb-1">
          <span class="inline-block h-2.5 w-2.5 rounded-full" :style="{ backgroundColor: focusedEvent.color }"></span>
          <span class="font-semibold">{{ focusedEvent.label }}</span>
          <!-- <span class="ml-auto text-xs text-zinc-500 dark:text-zinc-400">#{{ focusedEvent.id }}</span> -->
          <button v-if="focusedEventPinned" type="button"
            class="ml-auto text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200 size-4" aria-label="Close"
            @click="clearFocusedEvent">
            <IconClose class="size-4" />
          </button>
        </div>
        <div v-for="[label, value] in focusedEvent.rows" :key="label" class="flex justify-between gap-x-3">
          <div class="text-zinc-500 dark:text-zinc-400">{{ label }}</div>
          <div class="text-right">{{ value }}</div>
        </div>
      </div>
      <div class="absolute bottom-4 right-4 w-52 bg-zinc-100 dark:bg-zinc-800 rounded-md shadow">
        <div class="rounded-t-md bg-zinc-200 dark:bg-zinc-700 p-2 flex justify-between items-center">
          <h5 class="font-medium text-sm">Legend</h5>
        </div>
        <div class="p-2 text-sm">
          <ul>
            <li v-for="[key, type] in legendTypes" :key="key" class="flex items-center justify-between py-0.5">
              <div>{{ type.label }}</div>
              <svg v-if="arrowsAvailable" width="36" height="12" viewBox="0 0 36 12" aria-hidden="true">
                <line x1="2" y1="6" x2="26" y2="6" :stroke="type.color" stroke-width="3" stroke-linecap="round" />
                <polygon points="24,1 34,6 24,11 27,6" :fill="type.color" />
              </svg>
              <div v-else class="h-3.5 w-3.5 rounded-full" :style="{ backgroundColor: type.color }"></div>
            </li>
          </ul>
          <!-- <ul v-if="arrowsAvailable" class="mt-2 pt-2 border-t border-zinc-300 dark:border-zinc-600 text-xs text-zinc-600 dark:text-zinc-400 space-y-1">
            <li>Head = heading at end of event</li>
            <li>Tail = road travelled; length = distance during event</li>
            <li>Width = Max G</li>
            <li class="flex items-center justify-between">
              <span>Not matched to a road</span>
              <svg width="36" height="12" viewBox="0 0 36 12" aria-hidden="true">
                <line x1="2" y1="6" x2="26" y2="6" stroke="currentColor" stroke-width="3" stroke-dasharray="5 3" stroke-linecap="round" />
                <polygon points="24,1 34,6 24,11 27,6" fill="currentColor" />
              </svg>
            </li>
            <li v-if="belowArrowZoom">Zoom in to see arrows</li>
          </ul> -->
        </div>
      </div>
    </div>
  </div>
  <div class="h-[calc(100vh-178px)] flex place-items-center justify-center" v-else>
    <IconSpinner class="animate-spin w-12 h-12" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { useRoute } from "vue-router";
import { storeToRefs } from "pinia";
import { useAuth0 } from "@auth0/auth0-vue";
import { isEmpty, flatten, cloneDeep, get } from "lodash";
import { bbox } from "@turf/turf";
import AirSageMap from "@/lib/map";
import { EVENT_TYPES, UNKNOWN_EVENT_COLOR } from "@/lib/safety/constants";
import { buildSafetyGeoJSON } from "@/lib/safety/process";
import { StudiesService } from "@/services";
import ContentBlock from "@/components/ContentBlock.vue";
import MultipleObjectSelect from "@/components/MultipleObjectSelect.vue";
import RangeInput from "@/components/RangeInput.vue";
import HourRangeInput from "@/components/HourRangeInput.vue";
import IconSpinner from "@/components/icons/IconSpinner.vue";
import IconClose from "@/components/icons/IconClose.vue";
import { useStudyStore } from "@/stores/study";
import { useAlertStore } from "@/stores/alert";

const studyStore = useStudyStore();
const alertStore = useAlertStore();
const route = useRoute();
const { getAccessTokenSilently } = useAuth0();
const { config } = storeToRefs(studyStore);

// zoom at which dots are replaced by road-following arrows
const ARROW_MIN_ZOOM = 13;
const MAX_G_STEP = 0.05;
const SOURCE_ID = "safety-events";
const LAYERS = {
  dots: "safety-events-dots",
  arrows: "safety-events-arrows",
  arrowsUnmatched: "safety-events-arrows-unmatched",
  heads: "safety-events-heads"
};
// enlarged copies of the hovered/pinned event drawn above everything else
const HIGHLIGHT_LAYERS = {
  dots: "safety-events-dots-highlight",
  casing: "safety-events-arrows-highlight-casing",
  arrows: "safety-events-arrows-highlight",
  heads: "safety-events-heads-highlight"
};
const HIGHLIGHT_SCALE = 1.6;
const NO_EVENT_ID = -1;
// legacy vector tile source/layer, used when no browser-side data is published for the study
const TILE_SOURCE_ID = "points";
const TILE_SOURCE_LAYER = "safety-events";
const TILE_LAYER_ID = `${TILE_SOURCE_ID}-${TILE_SOURCE_LAYER}`;

const loaded = ref(false);
const processing = ref(false);
const processingMessage = ref("");
const arrowsAvailable = ref(false);
const belowArrowZoom = ref(true);
const stats = ref(null);
const maxGBounds = ref({ min: 0, max: 2 });
const focusedEvent = ref(null);
const focusedEventPinned = ref(false);
const hoveredEventId = ref(null);
const highlightedEventId = computed(() => {
  if (hoveredEventId.value !== null) {
    return hoveredEventId.value;
  }
  return focusedEventPinned.value && focusedEvent.value ? focusedEvent.value.id : null;
});

const legendTypes = Object.entries(EVENT_TYPES);
const eventTypesOptions = ref(legendTypes.map(([value, { label }]) => ({ name: label, value })));
const defaultFilter = () => ({
  eventTypes: [],
  maxG: { min: 0, max: 2 },
  hours: { min: 0, max: 23 }
});
const proxyFilter = ref(defaultFilter());
const filter = ref(defaultFilter());

const colorExpression = computed(() => [
  "match",
  ["get", "event_type"],
  ...flatten(legendTypes.map(([eventType, { color }]) => [eventType, color])),
  UNKNOWN_EVENT_COLOR
]);

let map = null;
let inputsPromise = null;
let eventsById = new Map();

/**
 * Fetch the roadway GeoJSON and events CSV uploaded to the study's
 * materialized-views folder (proxied by the API). Resolves to null when the
 * study has none, so the view can fall back to the legacy vector tiles.
 */
async function loadSafetyInputs(id) {
  const token = await getAccessTokenSilently();
  const options = (responseType) => ({
    headers: { Authorization: `Bearer ${token}` },
    responseType
  });
  try {
    const [roads, csvText] = await Promise.all([
      StudiesService.getSafetyInput(id, "roads", options("json")),
      StudiesService.getSafetyInput(id, "events", options("text"))
    ]);
    return { roads, csvText };
  } catch (error) {
    if (get(error, "response.status") === 404) {
      return null;
    }
    throw error;
  }
}

watch(config, async () => {
  if (!isEmpty(config.value)) {
    const { id } = route.params;
    // start downloading while the map initializes
    inputsPromise = loadSafetyInputs(id).catch((error) => {
      alertStore.alertError(error);
      return null;
    });
    loaded.value = true;
    await nextTick();
    proxyFilter.value.eventTypes = eventTypesOptions.value.map((et) => et.value);
    applyFilter();
    loadMap();
  }
});

onBeforeUnmount(studyStore.reset);

function applyFilter() {
  filter.value = cloneDeep(proxyFilter.value);
}

/** Expression that passes numeric values within [min, max] and leaves missing values visible. */
function rangeExpression(property, min, max) {
  const value = ["get", property];
  return [
    "case",
    ["==", ["typeof", value], "number"],
    ["all", [">=", value, min], ["<=", value, max]],
    true
  ];
}

function layerFilter(kind, { matched } = {}) {
  const parts = [
    "all",
    ["==", ["get", "kind"], kind],
    ["in", ["get", "event_type"], ["literal", filter.value.eventTypes]],
    rangeExpression("max_g", filter.value.maxG.min, filter.value.maxG.max),
    rangeExpression("hour", filter.value.hours.min, filter.value.hours.max)
  ];
  if (matched !== undefined) {
    parts.push(["==", ["get", "matched"], matched]);
  }
  return parts;
}

function highlightFilter(kind) {
  return ["all", layerFilter(kind), ["==", ["get", "event_id"], highlightedEventId.value ?? NO_EVENT_ID]];
}

function applyHighlightFilters() {
  if (!map || !map.getLayer(HIGHLIGHT_LAYERS.dots)) {
    return;
  }
  map.setFilter(HIGHLIGHT_LAYERS.dots, highlightFilter("event"));
  map.setFilter(HIGHLIGHT_LAYERS.casing, highlightFilter("arrow"));
  map.setFilter(HIGHLIGHT_LAYERS.arrows, highlightFilter("arrow"));
  map.setFilter(HIGHLIGHT_LAYERS.heads, highlightFilter("head"));
}

watch(highlightedEventId, applyHighlightFilters);

function applyLayerFilters() {
  if (!map) {
    return;
  }
  if (map.getLayer(TILE_LAYER_ID)) {
    map.setFilter(TILE_LAYER_ID, ["in", ["get", "event_type"], ["literal", filter.value.eventTypes]]);
  }
  if (map.getLayer(LAYERS.dots)) {
    map.setFilter(LAYERS.dots, layerFilter("event"));
    map.setFilter(LAYERS.arrows, layerFilter("arrow", { matched: true }));
    map.setFilter(LAYERS.arrowsUnmatched, layerFilter("arrow", { matched: false }));
    map.setFilter(LAYERS.heads, layerFilter("head"));
    applyHighlightFilters();
  }
}

watch(filter, applyLayerFilters, { deep: true });

function roundTo(value, step, fn) {
  return Number((fn(value / step) * step).toFixed(2));
}

function initializeMaxGFilter(geojson) {
  const values = geojson.features
    .filter((f) => f.properties.kind === "event" && Number.isFinite(f.properties.max_g))
    .map((f) => f.properties.max_g);
  if (!values.length) {
    return;
  }
  maxGBounds.value = {
    min: roundTo(Math.min(...values), MAX_G_STEP, Math.floor),
    max: roundTo(Math.max(...values), MAX_G_STEP, Math.ceil)
  };
  proxyFilter.value.maxG = { ...maxGBounds.value };
  applyFilter();
}

function addArrowLayers(geojson) {
  const arrowheadColors = {
    ...Object.fromEntries(legendTypes.map(([key, { color }]) => [key, color])),
    unknown: UNKNOWN_EVENT_COLOR
  };
  const casingColor = "rgba(255, 255, 255, 0.85)";
  const arrowheads = map.addArrowheadImages(arrowheadColors);
  // white-outlined variants for the highlighted event, matching the line casing
  const highlightArrowheads = map.addArrowheadImages(arrowheadColors, {
    prefix: "arrowhead-highlight",
    outlineColor: casingColor,
    outlineWidth: 1.5
  });

  map.addSource(SOURCE_ID, { type: "geojson", data: geojson });

  map.addLayer({
    id: LAYERS.dots,
    type: "circle",
    source: SOURCE_ID,
    maxzoom: ARROW_MIN_ZOOM,
    filter: layerFilter("event"),
    paint: {
      "circle-color": colorExpression.value,
      "circle-radius": 6,
      "circle-stroke-width": 1,
      "circle-stroke-color": "rgba(0, 0, 0, 0.4)"
    }
  });

  const lineWidth = ["interpolate", ["linear"], ["coalesce", ["get", "max_g"], 0.4], 0.4, 2.5, 1, 7];
  // butt caps so the line stops exactly at the arrowhead tip instead of extending half its width past it
  const lineLayout = { "line-cap": "butt", "line-join": "round" };
  map.addLayer({
    id: LAYERS.arrows,
    type: "line",
    source: SOURCE_ID,
    minzoom: ARROW_MIN_ZOOM,
    filter: layerFilter("arrow", { matched: true }),
    layout: lineLayout,
    paint: { "line-color": colorExpression.value, "line-width": lineWidth }
  });
  map.addLayer({
    id: LAYERS.arrowsUnmatched,
    type: "line",
    source: SOURCE_ID,
    minzoom: ARROW_MIN_ZOOM,
    filter: layerFilter("arrow", { matched: false }),
    layout: lineLayout,
    paint: { "line-color": colorExpression.value, "line-width": lineWidth, "line-dasharray": [1.5, 1.5], "line-opacity": 0.8 }
  });
  // Push the head forward (negative y = direction of travel once rotated) by about the line width,
  // so the wider line ends underneath the arrowhead body rather than showing beside its narrow tip.
  const headOffset = [
    "step", ["coalesce", ["get", "max_g"], 0.4],
    ["literal", [0, -4.5]],
    0.5, ["literal", [0, -5.5]],
    0.65, ["literal", [0, -6.5]],
    0.8, ["literal", [0, -7.5]],
    0.95, ["literal", [0, -9]]
  ];
  const headLayout = (scale, images) => ({
    "icon-image": [
      "match",
      ["get", "event_type"],
      ...flatten(legendTypes.map(([key]) => [key, images[key]])),
      images.unknown
    ],
    "icon-size": ["interpolate", ["linear"], ["zoom"], ARROW_MIN_ZOOM, 0.6 * scale, 16, scale],
    "icon-rotate": ["coalesce", ["get", "bearing"], 0],
    "icon-rotation-alignment": "map",
    // the arrowhead tip is drawn at the image centre, so centre-anchoring pins it to the line end
    "icon-anchor": "center",
    // icon-offset is multiplied by icon-size, so it scales with the line width below
    "icon-offset": headOffset,
    "icon-allow-overlap": true,
    "icon-ignore-placement": true
  });
  map.addLayer({
    id: LAYERS.heads,
    type: "symbol",
    source: SOURCE_ID,
    minzoom: ARROW_MIN_ZOOM,
    filter: layerFilter("head"),
    layout: headLayout(1, arrowheads)
  });

  // highlight layers: enlarged copies of the hovered/pinned event, above everything else
  const highlightWidth = ["*", lineWidth, HIGHLIGHT_SCALE];
  map.addLayer({
    id: HIGHLIGHT_LAYERS.dots,
    type: "circle",
    source: SOURCE_ID,
    maxzoom: ARROW_MIN_ZOOM,
    filter: highlightFilter("event"),
    paint: {
      "circle-color": colorExpression.value,
      "circle-radius": 6 * HIGHLIGHT_SCALE,
      "circle-stroke-width": 2,
      "circle-stroke-color": casingColor
    }
  });
  map.addLayer({
    id: HIGHLIGHT_LAYERS.casing,
    type: "line",
    source: SOURCE_ID,
    minzoom: ARROW_MIN_ZOOM,
    filter: highlightFilter("arrow"),
    layout: lineLayout,
    paint: { "line-color": casingColor, "line-width": ["+", highlightWidth, 4] }
  });
  map.addLayer({
    id: HIGHLIGHT_LAYERS.arrows,
    type: "line",
    source: SOURCE_ID,
    minzoom: ARROW_MIN_ZOOM,
    filter: highlightFilter("arrow"),
    layout: lineLayout,
    paint: { "line-color": colorExpression.value, "line-width": highlightWidth }
  });
  map.addLayer({
    id: HIGHLIGHT_LAYERS.heads,
    type: "symbol",
    source: SOURCE_ID,
    minzoom: ARROW_MIN_ZOOM,
    filter: highlightFilter("head"),
    layout: headLayout(HIGHLIGHT_SCALE, highlightArrowheads)
  });

  const interactiveLayers = [...Object.values(LAYERS), ...Object.values(HIGHLIGHT_LAYERS)];
  const featureAt = (point) => map.queryRenderedFeatures(point, { layers: interactiveLayers })[0] || null;

  map.on("mousemove", (e) => {
    const feature = featureAt(e.point);
    map.getCanvas().style.cursor = feature ? "pointer" : "";
    hoveredEventId.value = feature ? feature.properties.event_id : null;
    if (feature) {
      focusEvent(feature, false);
    } else if (!focusedEventPinned.value) {
      focusedEvent.value = null;
    }
  });
  map.on("mouseout", () => {
    map.getCanvas().style.cursor = "";
    hoveredEventId.value = null;
    if (!focusedEventPinned.value) {
      focusedEvent.value = null;
    }
  });
  map.on("click", (e) => {
    const feature = featureAt(e.point);
    if (feature) {
      focusEvent(feature, true);
    } else {
      // clicks on empty map unpin the details card
      clearFocusedEvent();
    }
  });

  belowArrowZoom.value = map.getZoom() < ARROW_MIN_ZOOM;
  map.on("zoomend", () => {
    belowArrowZoom.value = map.getZoom() < ARROW_MIN_ZOOM;
  });
}

function addTileLayers() {
  const { id } = route.params;
  map.addSource(TILE_SOURCE_ID, {
    type: "vector",
    tiles: [`${import.meta.env.VITE_TILE_CDN_URL}/${id}/{z}/{x}/{y}.pbf`],
    promoteId: "segment_id",
    minzoom: 0,
    maxzoom: 11
  });
  map.addLayer({
    id: TILE_LAYER_ID,
    type: "circle",
    source: TILE_SOURCE_ID,
    "source-layer": TILE_SOURCE_LAYER,
    paint: {
      "circle-color": colorExpression.value,
      "circle-radius": 6
    }
  });
}

const formatNumber = (value, digits = 0, suffix = "") => (Number.isFinite(value) ? `${value.toFixed(digits)}${suffix}` : "—");

function describeEvent(props) {
  const type = EVENT_TYPES[props.event_type];
  return {
    id: props.event_id,
    label: type ? type.label : props.event_type,
    color: type ? type.color : UNKNOWN_EVENT_COLOR,
    rows: [
      ["Time", props.end_time || "—"],
      ["Duration", formatNumber(props.duration_s, 0, " s")],
      ["Max G", formatNumber(props.max_g, 2, " g")],
      ["Speed", `${formatNumber(props.start_speed_mph)} → ${formatNumber(props.end_speed_mph)} mph`],
      ["Heading", formatNumber(props.heading, 0, "°")],
      ["Road", props.road_name || (props.matched ? "unnamed road" : "not matched")]
    ]
  };
}

function focusEvent(feature, pin) {
  if (focusedEventPinned.value && !pin) {
    return;
  }
  const event = feature && eventsById.get(feature.properties.event_id);
  if (!event) {
    return;
  }
  focusedEvent.value = describeEvent(event.properties);
  if (pin) {
    focusedEventPinned.value = true;
  }
}

function clearFocusedEvent() {
  focusedEvent.value = null;
  focusedEventPinned.value = false;
}

async function loadLayers() {
  processing.value = true;
  processingMessage.value = "Loading events and roadways…";
  const inputs = await inputsPromise;
  if (!map) {
    return;
  }

  if (inputs) {
    processingMessage.value = "Matching events to roads…";
    // let the overlay paint before blocking on the matching work
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const { geojson, stats: matchStats } = buildSafetyGeoJSON(inputs.roads, inputs.csvText);
    console.debug("safety events processed", matchStats);
    stats.value = matchStats;
    eventsById = new Map(geojson.features.filter((f) => f.properties.kind === "event").map((f) => [f.properties.event_id, f]));
    arrowsAvailable.value = true;
    initializeMaxGFilter(geojson);
    addArrowLayers(geojson);
    if (geojson.features.length) {
      map.centerOn(bbox(geojson), { easing: () => 1 });
    } else {
      map.centerOn(config.value.boundBox, { easing: () => 1 });
    }
  } else {
    addTileLayers();
    map.centerOn(config.value.boundBox, { easing: () => 1 });
  }

  applyLayerFilters();
  processing.value = false;
}

const mapElement = ref(null);
function loadMap() {
  map = new AirSageMap(mapElement.value, {
    eventHandlers: {
      "style.load": loadLayers
    }
  });
}
</script>
